/*
 * Copyright (c) 2025 Ipivot Corporation.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 *  Ipivot Corporation ("Confidential Information").
 */
import { Row, Col, ListGroup } from 'react-bootstrap';
import Container from 'react-bootstrap/Container';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from 'styled-components';
import { useEffect, useState } from 'react';
import { SettingsContext } from './SettingsContext';

function SettingsLayout() {
    const theme = useTheme() as any;
    const menuBorder = `1px solid ${theme?.colors?.ctext || '#ccc'}`;
    const navigate = useNavigate();
    const location = useLocation();
    const [isDirty, setIsDirty] = useState(false);

    const pathToCategory = (path: string) => {
        if (path.endsWith('/users')) return 'users';
        if (path.endsWith('/events')) return 'events';
        if (path.endsWith('/identity')) return 'identity';
        if (path.endsWith('/display')) return 'display';
        if (path.endsWith('/sounds')) return 'sounds';
        if (path.endsWith('/metadata')) return 'metadata';
        if (path.endsWith('/network')) return 'network';
        if (path.endsWith('/task-manager')) return 'task-manager';
        if (path.endsWith('/fuzzy-matching')) return 'fuzzy-matching';
        if (path.endsWith('/advanced')) return 'advanced';
        if (path.endsWith('/diagnostics')) return 'diagnostics';
        if (path.endsWith('/developer')) return 'developer';
        if (path.endsWith('/ops')) return 'ops';
        if (path.endsWith('/about')) return 'about';
        if (path.endsWith('/updates')) return 'updates';
        return 'display';
    };

    const activeCategory = pathToCategory(location.pathname);

    useEffect(() => {
        // On mount, navigate to the last selected tab or default to display
        if (location.pathname === '/settings' || location.pathname === '/settings/') {
            const lastTab = localStorage.getItem('fbot:settings:activeTab') || 'display';
            navigate(`/settings/${lastTab}`, { replace: true });
        }
    }, []);

    const handleCategoryClick = (category: string) => {
        if (isDirty) {
            return;
        }
        setIsDirty(false);
        localStorage.setItem('fbot:settings:activeTab', category);
        navigate(`/settings/${category}`);
    };

    return (
        <Container fluid>
            <Row style={{ height: '90vh' }}>
                <Col md={3} lg={1} className="pt-3 pe-0" style={{ borderRight: menuBorder }}>
                    <div className="px-3 pb-2">
                        <h5 className="mb-1">Settings</h5>
                    </div>
                    <ListGroup variant="flush">
                        <ListGroup.Item
                            action
                            active={activeCategory === 'users'}
                            onClick={() => handleCategoryClick('users')}
                            style={{ color: theme?.colors?.ctext || 'lime' }}
                        >
                            Users
                        </ListGroup.Item>
                        <ListGroup.Item
                            action
                            active={activeCategory === 'events'}
                            onClick={() => handleCategoryClick('events')}
                            style={{ color: theme?.colors?.ctext || 'lime' }}
                        >
                            Events
                        </ListGroup.Item>
                        <ListGroup.Item
                            action
                            active={activeCategory === 'identity'}
                            onClick={() => handleCategoryClick('identity')}
                            style={{ color: theme?.colors?.ctext || 'lime' }}
                        >
                            Identity
                        </ListGroup.Item>
                        <ListGroup.Item
                            action
                            active={activeCategory === 'display'}
                            onClick={() => handleCategoryClick('display')}
                            style={{ color: theme?.colors?.ctext || 'lime' }}
                        >
                            Display
                        </ListGroup.Item>
                        <ListGroup.Item
                            action
                            active={activeCategory === 'sounds'}
                            onClick={() => handleCategoryClick('sounds')}
                            style={{ color: theme?.colors?.ctext || 'lime' }}
                        >
                            Sounds
                        </ListGroup.Item>
                        <ListGroup.Item
                            action
                            active={activeCategory === 'metadata'}
                            onClick={() => handleCategoryClick('metadata')}
                            style={{ color: theme?.colors?.ctext || 'lime' }}
                        >
                            Metadata
                        </ListGroup.Item>
                        <ListGroup.Item
                            action
                            active={activeCategory === 'network'}
                            onClick={() => handleCategoryClick('network')}
                            style={{ color: theme?.colors?.ctext || 'lime' }}
                        >
                            Network
                        </ListGroup.Item>
                        <ListGroup.Item
                            action
                            active={activeCategory === 'task-manager'}
                            onClick={() => handleCategoryClick('task-manager')}
                            style={{ color: theme?.colors?.ctext || 'lime' }}
                        >
                            Task Manager
                        </ListGroup.Item>
                        <ListGroup.Item
                            action
                            active={activeCategory === 'fuzzy-matching'}
                            onClick={() => handleCategoryClick('fuzzy-matching')}
                            style={{ color: theme?.colors?.ctext || 'lime' }}
                        >
                            Fuzzy Matching
                        </ListGroup.Item>
                        <ListGroup.Item
                            action
                            active={activeCategory === 'advanced'}
                            onClick={() => handleCategoryClick('advanced')}
                            style={{ color: theme?.colors?.ctext || 'lime' }}
                        >
                            Advanced
                        </ListGroup.Item>
                        <ListGroup.Item
                            action
                            active={activeCategory === 'diagnostics'}
                            onClick={() => handleCategoryClick('diagnostics')}
                            style={{ color: theme?.colors?.ctext || 'lime' }}
                        >
                            Diagnostics
                        </ListGroup.Item>
                        <ListGroup.Item
                            action
                            active={activeCategory === 'developer'}
                            onClick={() => handleCategoryClick('developer')}
                            style={{ color: theme?.colors?.ctext || 'lime' }}
                        >
                            Developer
                        </ListGroup.Item>
                        <ListGroup.Item
                            action
                            active={activeCategory === 'ops'}
                            onClick={() => handleCategoryClick('ops')}
                            style={{ color: theme?.colors?.ctext || 'lime' }}
                        >
                            Ops
                        </ListGroup.Item>
                        <ListGroup.Item
                            action
                            active={activeCategory === 'updates'}
                            onClick={() => handleCategoryClick('updates')}
                            style={{ color: theme?.colors?.ctext || 'lime' }}
                        >
                            Updates
                        </ListGroup.Item>
                        <ListGroup.Item
                            action
                            active={activeCategory === 'about'}
                            onClick={() => handleCategoryClick('about')}
                            style={{ color: theme?.colors?.ctext || 'lime' }}
                        >
                            About
                        </ListGroup.Item>
                    </ListGroup>
                </Col>
                <Col md={9} lg={10} className="pt-4 pb-4 px-4" style={{ height: '90vh', overflow: 'auto' }}>
                    <SettingsContext.Provider value={{ isDirty, setIsDirty }}>
                        <Outlet />
                    </SettingsContext.Provider>
                </Col>
            </Row>
        </Container>
    );
}

export default SettingsLayout;
